// Полоса проверки над сеткой ведомости: одна фраза, число и переход.
//
// Это единственный вопрос, ради которого экран открывают перед закрытием месяца: «можно ли
// утверждать». Ответ обязан быть виден без единого нажатия, поэтому пометки приезжают в ТОМ ЖЕ
// ответе, что и сетка (`review_notes` у каждого человека), а не отдельным запросом: посчитанные
// отдельно, они разойдутся с сеткой на любой параллельной правке, и человек перестанет верить
// обеим.
//
// ## Когда замечаний нет, это пишется СЛОВОМ
//
// Ноль в счётчике читается как «не считали», а молчание — как «здесь ничего не проверяют».
// Поэтому «замечаний нет» — отдельная фраза, а не «0 замечаний».
//
// ## Два разных нуля не слипаются
//
// Обнуление каскадом — замечание: не хватило начисленного на аванс, остаток ушёл в долг.
// Ноль оттого, что начисления равны официальной части, — штатное дело, и в полосу он не
// попадает вовсе (сценарий 84). Общий признак «итог нулевой» превратил бы полосу в шум, и её
// перестали бы читать.

import { RiCheckboxCircleLine, RiErrorWarningLine, RiInformationLine } from '@remixicon/react';

import type { PayrollEmployeeOut, PayrollTotalsOut } from '@/shared/api/endpoints/payroll';
import { countLabel } from '@/shared/text/plural';
import * as Button from '@/shared/ui/button';

export interface SheetReview {
  /** Расчёт по ведомости проводился хотя бы у кого-то. */
  readonly calculated: boolean;
  /** Людей с пометками — столько строк увидит человек в разборе. */
  readonly flagged: number;
  /** Из них обнулено каскадом. */
  readonly zeroedByCascade: number;
  /** Из них нет в связанном официальном периоде. */
  readonly noOfficialRow: number;
  /**
   * Строк без суммы за период или нормы часов.
   *
   * Считается сервером, а не выводится из нулей: пустой тариф и честный ноль в браузере не
   * различимы. Стоит отдельно от «без расчёта» потому, что совет разный: там «пересчитайте»,
   * здесь «пересчёт не поможет, заполните тариф».
   */
  readonly rowsWithoutRate: number;
  /** Людей, по которым расчёта ещё не было. Ведомость «посчитана» — не значит «вся». */
  readonly notCalculated: number;
  /** Строк выведено из расчёта с основанием. Замечанием не является. */
  readonly excludedRows: number;
  /**
   * Официальный период за месяц не связан с ведомостью: официальная часть не вычтена вовсе.
   *
   * До 23.09.2026 полоса в этом случае писала «Замечаний нет: ни … людей без строки в
   * официальном периоде» — сервер намеренно не ставит людям эту пометку, когда период не связан,
   * а полоса читала её отсутствие как «всё проверено». На деле к выплате у всех, кто оформлен
   * официально, завышено на официальную часть: заплатить по такой ведомости значит заплатить её
   * второй раз.
   */
  readonly officialUnlinked: boolean;
  /** Замечаний нет — и это надо сказать словом, а не нулём. */
  readonly nothingToCheck: boolean;
}

/**
 * Требует ли человек разбора до утверждения.
 *
 * Один предикат на полосу и на панель разбора: два похожих условия в двух местах однажды
 * разойдутся, и полоса скажет «трое», а в панели их окажется двое.
 */
export function требуетРазбора(человек: PayrollEmployeeOut): boolean {
  return (
    человек.review_notes.length > 0 ||
    человек.flag_zeroed_by_cascade ||
    человек.no_official_row ||
    человек.calculated_at === null
  );
}

/**
 * Собрать полосу из того же ответа, что и сетка.
 *
 * Считается по пометкам СЕРВЕРА, а не по нулям в браузере: отличить ноль по каскаду от ноля от
 * равенства начислений официальной части можно только флагом, который ставит движок.
 */
export function buildReview(
  employees: readonly PayrollEmployeeOut[] | undefined,
  totals: PayrollTotalsOut,
  calculated: boolean,
  rowsWithoutRate = 0,
  officialLinked = true,
): SheetReview {
  const люди = employees ?? [];
  // Человек попадает в разбор по ЛЮБОМУ признаку, а не только по готовой пометке. Пометки
  // собирает сервер, но флаг он ставит отдельно, и если они разойдутся, зелёная галочка встанет
  // над серыми нулями в сетке — а по ней утверждают ведомость.
  const требуютРазбора = люди.filter(требуетРазбора);
  return {
    calculated,
    flagged: требуютРазбора.length,
    zeroedByCascade: люди.filter((человек) => человек.flag_zeroed_by_cascade).length,
    noOfficialRow: люди.filter((человек) => человек.no_official_row).length,
    notCalculated: люди.filter((человек) => человек.calculated_at === null).length,
    excludedRows: totals.excluded_rows,
    rowsWithoutRate,
    officialUnlinked: !officialLinked,
    nothingToCheck:
      calculated && требуютРазбора.length === 0 && rowsWithoutRate === 0 && officialLinked,
  };
}

export function ReviewBar({
  review,
  onOpen,
  onTariffs,
}: {
  review: SheetReview;
  /** Переход в разбор замечаний. */
  onOpen: () => void;
  /** Переход в лист тарифов по ЭТОМУ объекту. Пусто — перехода нет. */
  onTariffs?: () => void;
}) {
  // Расчёта не было — это не «всё чисто». Зелёная галочка здесь сказала бы, что ведомость
  // проверена, при том что проверять в ней ещё нечего: суммы неизвестны, а не равны нулю.
  if (!review.calculated) {
    // ⚠️ Раньше здесь стояло «проверять пока нечего» — и ни одного действия. Заказчик
    // 16.09.2026 смотрел ровно на такую полосу и не знал, что делать дальше. Правило проекта:
    // блокировка объясняет причину И предлагает шаг.
    return (
      <div className='flex items-center gap-2 border-b border-stroke-soft-200 bg-bg-weak-50 px-5 py-2'>
        <RiInformationLine className='size-4 shrink-0 text-text-soft-400' />
        <span className='min-w-0 text-paragraph-sm text-text-strong-950'>
          Расчёт по этой ведомости ещё не проводился: суммы в колонках не нули, а неизвестные
          величины.
          {review.rowsWithoutRate > 0 && (
            <span className='pl-1 text-text-sub-600'>
              Тариф не заполнен в {review.rowsWithoutRate}: без суммы за период и нормы часов
              начислять не из чего, и пересчёт этого не изменит.
            </span>
          )}
          {review.excludedRows > 0 && (
            <span className='pl-1 text-text-sub-600'>
              Строк выведено из расчёта с основанием: {review.excludedRows}.
            </span>
          )}
        </span>
        {review.rowsWithoutRate > 0 && onTariffs !== undefined && (
          <Button.Root
            variant='primary'
            mode='stroke'
            size='xxsmall'
            className='shrink-0'
            onClick={onTariffs}
          >
            Заполнить тариф
          </Button.Root>
        )}
      </div>
    );
  }

  if (review.nothingToCheck) {
    return (
      <div className='flex items-center gap-2 border-b border-stroke-soft-200 bg-bg-weak-50 px-5 py-2'>
        <RiCheckboxCircleLine className='size-4 shrink-0 text-success-base' />
        <span className='min-w-0 text-paragraph-sm text-text-strong-950'>
          {/* Перечень стоял ПЕРЕД глаголом: «итогов…, людей…, строк… не нашлось» — три
              вещи надо было удержать в голове, прежде чем узнать, что с ними. Строй «ни…
              ни… ни…» после ответа читается сразу, а сведений в нём столько же. */}
          Замечаний нет: ни обнулённых каскадом итогов, ни людей без строки в официальном периоде,
          ни непосчитанных строк.
          {review.excludedRows > 0 && (
            <span className='pl-1 text-text-sub-600'>
              Строк выведено из расчёта с основанием: {review.excludedRows} — это штатное дело, а не
              замечание.
            </span>
          )}
        </span>
      </div>
    );
  }

  const части: string[] = [];
  if (review.zeroedByCascade > 0) {
    // Не «1 итог обнулено»: число и глагол обязаны согласоваться, иначе фраза читается как
    // машинная. Родительный падеж после двоеточия работает при любом числе.
    части.push(`обнулено каскадом итогов: ${review.zeroedByCascade}`);
  }
  if (review.noOfficialRow > 0) {
    части.push(`нет в официальном периоде: ${review.noOfficialRow}`);
  }
  if (review.notCalculated > 0) {
    // Ведомость «рассчитана» — это про ведомость целиком. Три непосчитанных человека среди 281
    // выглядят прочерками в сетке, и зелёная галочка над ними была бы прямым враньём.
    части.push(`без расчёта: ${review.notCalculated}`);
  }
  if (review.rowsWithoutRate > 0) {
    // Пересчёт этого не изменит, и сказано это рядом с кнопкой, которая изменит.
    части.push(`тариф не заполнен в строках: ${review.rowsWithoutRate}`);
  }
  if (review.officialUnlinked) {
    части.push(
      'официальный период не связан — официальная часть не вычтена, к выплате завышено на неё',
    );
  }

  return (
    <div className='flex items-center gap-2 border-b border-stroke-soft-200 bg-bg-weak-50 px-5 py-2'>
      <RiErrorWarningLine className='size-4 shrink-0 text-warning-base' />
      <span className='min-w-0 text-paragraph-sm text-text-strong-950'>
        {review.flagged > 0 ? (
          <>
            Требуют разбора до утверждения:{' '}
            {countLabel(review.flagged, ['человек', 'человека', 'человек'])}
          </>
        ) : (
          'Проверьте до утверждения'
        )}
        {части.length > 0 && <span className='text-text-sub-600'> — {части.join(', ')}</span>}.
      </span>
      {/* До 23.09.2026 кнопка тарифов жила только у НЕпосчитанной ведомости. У посчитанной
          частично — ровно случай заказчика, где у 204 из 222 работавших нет тарифа, — путь к
          листу тарифов был только через разбор и текст «поправьте назначение сотрудника». */}
      {review.rowsWithoutRate > 0 && onTariffs !== undefined && (
        <Button.Root
          variant='primary'
          mode='stroke'
          size='xxsmall'
          className='shrink-0'
          onClick={onTariffs}
        >
          Заполнить тариф
        </Button.Root>
      )}
      {review.flagged > 0 && (
        <Button.Root
          variant='primary'
          mode='stroke'
          size='xxsmall'
          className='shrink-0'
          onClick={onOpen}
        >
          Разобрать
        </Button.Root>
      )}
    </div>
  );
}
